import React, { useState } from "react";
import { IDog } from "@eagerdog/interfaces";
import { TITLE_TRACKERS, TITLE_TRACKER_IDS, TitleTrackerId, TitleTrackerVisibilitySettings } from "@eagerdog/constants";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import Button from "../../../components/Button/Button";

import "./TrackerSettingsModal.scss";

interface IProps {
  dog: IDog;
  onClose: () => void;
  onSave: (updatedDog: IDog) => void;
}

const TrackerSettingsModal: React.FC<IProps> = ({ dog, onClose, onSave }) => {
  // Initialize with current settings, defaulting to true (visible) if not set
  const [settings, setSettings] = useState<TitleTrackerVisibilitySettings>(() => {
    const initialSettings: TitleTrackerVisibilitySettings = {};
    TITLE_TRACKER_IDS.forEach(trackerId => {
      initialSettings[trackerId] = dog.tracker_visibility_settings?.[trackerId] ?? true;
    });
    return initialSettings;
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = (trackerId: TitleTrackerId) => {
    setSettings(prev => ({
      ...prev,
      [trackerId]: !prev[trackerId]
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedDog = await apiService.updateDogTrackerVisibility(dog._id, settings);
      
      toast.show({
        title: "Settings Saved",
        content: "Tracker visibility settings updated successfully",
        type: "success",
        duration: 3000
      });

      onSave(updatedDog);
      onClose();
    } catch (error: any) {
      console.error('Error saving tracker visibility:', error);
      toast.show({
        title: "Save Failed",
        content: error.response?.data?.message || "Failed to update tracker visibility settings",
        type: "fail",
        duration: 5000,
        errorDetails: error
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="trackerSettingsModal">
      <div className="modalHeader">
        <h2>Tracker Display Settings</h2>
        <p className="subtitle">Choose which trackers to display on {dog.call_name}'s Ribbon Room</p>
      </div>

      <div className="modalBody">
        <div className="trackerList">
          {TITLE_TRACKER_IDS.map(trackerId => {
            const config = TITLE_TRACKERS[trackerId];
            const isVisible = settings[trackerId] ?? true;

            return (
              <div key={trackerId} className="trackerItem">
                <div className="trackerInfo">
                  <div className="trackerName">{config.label}</div>
                </div>
                <label className="toggleSwitch">
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => handleToggle(trackerId)}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div className="modalFooter">
        <Button
          onClick={onClose}
          variant="secondary"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="default"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default TrackerSettingsModal;

